import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Ruler, Plus, Trash2, Check, Package } from "lucide-react";
import {
  getSelectedSizeLabels,
  getSuggestedSizeGroups,
} from "../../../utils/fashionSizes";

/**
 * Optional size + stock picker for Fashion & Accessories deals.
 * Each selected size carries its own quantity.
 * Payload shape: [{ size: "M", quantity: "20" }, ...]
 */
const FashionSizesField = ({ sizes = [], subcategory = "", onChange }) => {
  const [customSize, setCustomSize] = useState("");
  const [activeGroup, setActiveGroup] = useState(null);
  const groups = useMemo(
    () => getSuggestedSizeGroups(subcategory),
    [subcategory]
  );

  const currentGroupId = activeGroup || groups[0]?.id;
  const currentGroup =
    groups.find((g) => g.id === currentGroupId) || groups[0];

  const selected = Array.isArray(sizes) ? sizes : [];
  const selectedLabels = getSelectedSizeLabels(selected);

  const isSelected = (size) =>
    selectedLabels.some((s) => s.toLowerCase() === String(size).toLowerCase());

  const toggleSize = (size) => {
    if (isSelected(size)) {
      onChange(
        selected.filter(
          (row) =>
            String(row?.size || "").toLowerCase() !== String(size).toLowerCase()
        )
      );
      return;
    }
    onChange([...selected, { size, quantity: "1" }]);
  };

  const updateQuantity = (size, quantity) => {
    const cleaned = String(quantity).replace(/\D/g, "").slice(0, 6);
    onChange(
      selected.map((row) =>
        String(row.size).toLowerCase() === String(size).toLowerCase()
          ? { ...row, quantity: cleaned === "" ? "" : cleaned }
          : row
      )
    );
  };

  const addCustom = () => {
    const next = customSize.trim();
    if (!next) return;
    if (isSelected(next)) {
      setCustomSize("");
      return;
    }
    onChange([...selected, { size: next, quantity: "1" }]);
    setCustomSize("");
  };

  const clearAll = () => onChange([]);

  const totalUnits = selected.reduce(
    (sum, row) => sum + (Number(row.quantity) || 0),
    0
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.2 }}
      className="rounded-xl border border-dashed border-[#FF6C6C]/35 bg-gradient-to-br from-[#FFF8F8] to-white p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="flex gap-3 items-start min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#FF6C6C]/10">
            <Ruler className="w-4 h-4 text-[#FF6C6C]" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Available sizes & stock
              </h4>
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Optional
              </span>
            </div>
            <p className="mt-0.5 text-xs text-gray-500 leading-relaxed">
              Select each size and set units for that size. Total stock is
              calculated automatically. Skip sizes if they do not apply — then
              set Quantity Available below yourself.
            </p>
          </div>
        </div>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs font-medium text-gray-400 hover:text-[#FF6C6C] transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Group tabs */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {groups.map((group) => {
          const isActive = group.id === currentGroup?.id;
          return (
            <button
              key={group.id}
              type="button"
              onClick={() => setActiveGroup(group.id)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-all ${
                isActive
                  ? "bg-[#FF6C6C] text-white shadow-sm"
                  : "bg-white text-gray-600 border border-gray-200 hover:border-[#FF6C6C]/40"
              }`}
            >
              {group.label}
            </button>
          );
        })}
      </div>

      {/* Preset chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {(currentGroup?.options || []).map((size) => {
          const active = isSelected(size);
          return (
            <button
              key={size}
              type="button"
              onClick={() => toggleSize(size)}
              aria-pressed={active}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-all ${
                active
                  ? "border-[#FF6C6C] bg-[#FF6C6C] text-white shadow-sm"
                  : "border-gray-200 bg-white text-gray-700 hover:border-[#FF6C6C]/50 hover:text-[#FF6C6C]"
              }`}
            >
              {active && <Check className="w-3.5 h-3.5" strokeWidth={2.5} />}
              {size}
            </button>
          );
        })}
      </div>

      {/* Custom size */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          type="text"
          value={customSize}
          onChange={(e) => setCustomSize(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder="Add a custom size (e.g. UK 8, 28W)"
          maxLength={40}
          className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:border-[#FF6C6C] focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20"
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={!customSize.trim()}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-gray-900 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Plus className="w-4 h-4" />
          Add size
        </button>
      </div>

      {/* Selected sizes with quantity */}
      <AnimatePresence>
        {selected.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Stock by size ({selected.length})
                </p>
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500">
                  <Package className="w-3 h-3" />
                  {totalUnits.toLocaleString()} total units
                </span>
              </div>

              <div className="overflow-hidden rounded-xl border border-gray-100 bg-white divide-y divide-gray-50">
                {selected.map((row) => (
                  <div
                    key={row.size}
                    className="flex flex-wrap items-center gap-3 px-3 py-2.5 sm:flex-nowrap"
                  >
                    <div className="flex h-9 w-12 shrink-0 items-center justify-center rounded-lg bg-[#FF6C6C]/10 text-sm font-bold text-[#FF6C6C]">
                      {row.size}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900">
                        Size {row.size}
                      </p>
                      <p className="text-[11px] text-gray-400">
                        Units available for this size
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="sr-only" htmlFor={`size-qty-${row.size}`}>
                        Quantity for {row.size}
                      </label>
                      <input
                        id={`size-qty-${row.size}`}
                        type="text"
                        inputMode="numeric"
                        value={row.quantity ?? ""}
                        onChange={(e) => updateQuantity(row.size, e.target.value)}
                        placeholder="0"
                        className="w-20 rounded-lg border border-gray-200 px-3 py-2 text-center text-sm font-semibold tabular-nums text-gray-900 focus:border-[#FF6C6C] focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20"
                      />
                      <button
                        type="button"
                        onClick={() => toggleSize(row.size)}
                        aria-label={`Remove size ${row.size}`}
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FashionSizesField;
